import type { StarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { extractSharingResource } from '#src/app/pages/starterDashboardV2/shared/contexts';
import type { MappedSharingResource } from '#src/app/pages/starterDashboardV2/shared/contexts';
import type {
	FacebookShareRecommendations,
	PostTypeDataMap,
	RedditShareRecommendations,
	Template,
} from '#src/app/pages/starterDashboardV2/shared/hooks';
import type { ShareVisualImg } from '#src/app/pages/starterDashboardV2/shared/types';
import type { EnrichedReadyToSharePlatformPostV2 } from '#src/app/shared/api/readyToShare';

import type { RecommendedGroupWithPlatform } from './types';

export type SharingPostData = {
	postTypeName: EnrichedReadyToSharePlatformPostV2['__typename'];
	sharingResource: MappedSharingResource;
};

export type SharingToolsData = {
	recommendedGroups: {
		isLoading: boolean;
		groups: RecommendedGroupWithPlatform[];
	};
	readyToSharePosts: {
		isLoading: boolean;
		facebookPost?: SharingPostData;
		redditPost?: SharingPostData;
		instagramStory?: SharingPostData;
	};
	shareVisual: {
		isLoading: boolean;
		image?: {
			url: string;
			variationName: string;
		};
	};
	posters: {
		isLoading: boolean;
		template?: {
			name: string;
			images: Template['images'];
		};
	};
};

export type SharingToolsActions = {
	navigateToAllTools: () => void;
	navigateToGroupRecommendations: () => void;
	navigateToReadyToSharePosts: () => void;
	navigateToShareVisual: () => void;
	navigateToPosters: () => void;
	navigateToEditPetition: () => void;
};

type BuildSharingToolsDataParams = {
	recommendedGroupsData: {
		shareRecommendations: { reddit: RedditShareRecommendations; facebook: FacebookShareRecommendations };
		shareRecommendationsLoading: boolean;
	};
	readyToSharePostsData: {
		isLoading: boolean;
		postTypeData: PostTypeDataMap;
	};
	shareVisualData: { shareVisualsState: string; items?: ShareVisualImg[] };
	flyerTemplates: Template[];
	petition: StarterDashboardV2PageData['petition'];
	options?: { imageVariantName?: string };
};

function buildRecommendedGroupsArray(
	reddit: RedditShareRecommendations,
	facebook: FacebookShareRecommendations,
): RecommendedGroupWithPlatform[] {
	const result: RecommendedGroupWithPlatform[] = [];

	// If there is at least one reddit group, that should be the first element
	if (reddit.length > 0) {
		result.push({ group: reddit[0], platform: 'reddit' });

		// If there are reddit and facebook group recommendations, return the first reddit group, and the first facebook group
		if (facebook.length > 0) {
			result.push({ group: facebook[0], platform: 'facebook' });
		}
		// If there's more than 1 reddit recommendation, but no facebook recommendations, return the 2 first reddit recommendations
		else if (reddit.length > 1) {
			result.push({ group: reddit[1], platform: 'reddit' });
		}
	}
	// If there are no reddit recommendations, but there are facebook recommendations, use facebook instead
	else if (facebook.length > 0) {
		result.push({ group: facebook[0], platform: 'facebook' });
		if (facebook.length > 1) {
			result.push({ group: facebook[1], platform: 'facebook' });
		}
	}

	return result;
}

function mapReadyToSharePosts(
	petition: StarterDashboardV2PageData['petition'],
	postTypeData: PostTypeDataMap,
): Omit<SharingToolsData['readyToSharePosts'], 'isLoading'> {
	const facebookPost = postTypeData.FACEBOOK_POST?.posts[0];
	const redditPost = postTypeData.REDDIT_GROUPS_POST?.posts[0];
	const instagramStory = postTypeData.INSTAGRAM_STORY?.posts[0];
	return {
		facebookPost: facebookPost
			? {
					postTypeName: facebookPost.__typename,
					sharingResource: extractSharingResource(facebookPost, { petition }),
				}
			: undefined,
		redditPost: redditPost
			? {
					postTypeName: redditPost.__typename,
					sharingResource: extractSharingResource(redditPost, { petition }),
				}
			: undefined,
		instagramStory: instagramStory
			? {
					postTypeName: instagramStory.__typename,
					sharingResource: extractSharingResource(instagramStory, { petition }),
				}
			: undefined,
	};
}

function mapShareVisualImage(shareVisualImages?: ShareVisualImg[], imageVariantName = 'post_variant_c') {
	const bestOption = shareVisualImages?.find((image) => image.variantName === imageVariantName);
	if (bestOption) {
		return { url: bestOption.image.src, variationName: bestOption.variantName };
	}
	const shareVisualPostImage = shareVisualImages?.find((image) => image.variantName.startsWith('post'));
	if (shareVisualPostImage) {
		return { url: shareVisualPostImage.image.src, variationName: shareVisualPostImage.variantName };
	}
	return undefined;
}

function findFlyerTemplate(flyerTemplates: Template[]) {
	return (
		flyerTemplates.find(({ name, enabled }) => name === 'flyer-b' && enabled) ??
		flyerTemplates.find(({ enabled }) => enabled) ??
		undefined
	);
}

export function buildSharingToolsData(params: BuildSharingToolsDataParams): SharingToolsData {
	const { recommendedGroupsData, readyToSharePostsData, shareVisualData, flyerTemplates, petition, options } = params;
	const mappedReadyToSharePosts = mapReadyToSharePosts(petition, readyToSharePostsData.postTypeData);
	const recommendedGroupsArray = buildRecommendedGroupsArray(
		recommendedGroupsData.shareRecommendations.reddit,
		recommendedGroupsData.shareRecommendations.facebook,
	);
	const shareVisualImage = mapShareVisualImage(shareVisualData.items, options?.imageVariantName);
	const template = findFlyerTemplate(flyerTemplates);

	return {
		recommendedGroups: {
			isLoading: recommendedGroupsData.shareRecommendationsLoading,
			groups: recommendedGroupsArray,
		},
		readyToSharePosts: {
			isLoading: readyToSharePostsData.isLoading,
			...mappedReadyToSharePosts,
		},
		shareVisual: {
			isLoading: shareVisualData.shareVisualsState === 'loading',
			image: shareVisualImage,
		},
		posters: {
			isLoading: false,
			template: template ? { name: template.name, images: template.images } : undefined,
		},
	};
}
